import type { Block } from '@/types/experience';

import { BLOCK_META } from '@/components/task/block-library';

function typeLabelOf(type: Block['type'], t: (key: string) => string): string {
  const meta = BLOCK_META[type];
  return meta ? t(meta.labelKey) : type;
}

function nextAvailableName(typeLabel: string, existingNames: Set<string>): string {
  let n = 1;
  while (existingNames.has(`${typeLabel} ${n}`)) n++;
  return `${typeLabel} ${n}`;
}

/**
 * How long a block name may be.
 *
 * The name is what a teacher will read in a CROSS-REFERENCE later — in a Flow condition, a change
 * card, a sensor row, eventually an export column. "GPS 1" does not survive that trip; "GPS -
 * Bromeliad Trail Start" does. So the length has to fit a descriptive phrase, and nothing beyond
 * one: this is a label, not a sentence.
 *
 * 40, sized against that phrase in both languages this app ships. The English form runs 27-31
 * characters; the Portuguese runs longer, and "bússola - fim da Trilha das Bromelias" — built from
 * a task title that exists in the real data — is 37. 40 clears that with a little room and stops
 * well short of prose.
 *
 * NOT sized from the stored data, which was the first instinct and the wrong one. The long names
 * in the database are deliberate attempts to break the formatting, so they measure what the owner
 * types while testing rather than what a teacher writes.
 *
 * It is also not a layout fix, and should not be mistaken for one. Flow's panel is 352px and a
 * name shares its line with "is" and a chip, so roughly 25 characters wrap there whatever this
 * says. The cap exists to bound the pathological case and to protect the surfaces where CSS
 * truncation cannot reach — `chipsToPlainText`, interpolated `aria-label`s, a CSV column.
 */
export const MAX_BLOCK_NAME_LENGTH = 40;

export function isNameTaken(name: string, blocks: Block[], excludeBlockId?: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;
  return blocks.some((b) => b.id !== excludeBlockId && b.name === trimmed);
}

/** Next unique auto-name for a newly created/duplicated block of this type — e.g. "Dropdown 2"
 * when "Dropdown 1" already exists. Checked against every current name (not a same-type count),
 * so it can never collide even if earlier blocks were renamed away from their own auto-pattern. */
export function computeAutoBlockName(type: Block['type'], blocks: Block[], t: (key: string) => string): string {
  const existingNames = new Set(blocks.map((b) => b.name).filter((n): n is string => !!n));
  return nextAvailableName(typeLabelOf(type, t), existingNames);
}

/** Assigns an auto name to every block missing one, in array order — a one-time backfill for
 * blocks that predate this feature. Frozen once assigned: never recomputed on later reorders/
 * deletions, so a block never appears to rename itself out of nowhere. */
export function backfillBlockNames(blocks: Block[], t: (key: string) => string): Block[] {
  const existingNames = new Set(blocks.map((b) => b.name).filter((n): n is string => !!n));
  return blocks.map((block) => {
    if (block.name) return block;
    const name = nextAvailableName(typeLabelOf(block.type, t), existingNames);
    existingNames.add(name);
    return { ...block, name };
  });
}
